/**
 * Timezone Utilities
 * Handles timezone conversion and formatting using Intl API
 */

/**
 * Converts a UTC date to a date in the specified timezone
 * Note: The returned Date object will technically have the system's timezone offset,
 * but the time components (hours, minutes, etc.) will match the target timezone.
 * This is primarily useful for extraction and display, not for further date math unless careful.
 * 
 * @param date - UTC Date object or ISO string
 * @param timezoneId - IANA timezone ID (e.g., "America/New_York")
 * @returns Date object reflecting the time in the target timezone
 */
export const convertByTimezone = (date: Date | string, timezoneId: string): Date => {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
        throw new Error('Invalid date provided to convertByTimezone');
    }

    // Use Intl.DateTimeFormat to get the parts in the target timezone
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezoneId,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        fractionalSecondDigits: 3,
        hour12: false,
    }).formatToParts(d);

    // Reconstruct the date from parts
    const partValues: Record<string, number> = {};
    for (const part of parts) {
        if (part.type !== 'literal') {
            partValues[part.type] = parseInt(part.value, 10);
        }
    }

    // Create a new Date object with the components from the timezone
    // Note: We use the local constructor, so the resulting Date object will "look" like
    // the time in the timezone, but its internal timestamp will be shifted.
    // This is a "zoneless" date approach commonly used when full timezone libs aren't available.
    return new Date(
        partValues.year,
        partValues.month - 1,
        partValues.day,
        partValues.hour % 24,
        partValues.minute,
        partValues.second
    );
};

/**
 * Formats a date in a specific timezone
 * 
 * @param date - Date object or ISO string
 * @param timezoneId - IANA timezone ID
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted string
 */
export const formatInTimeZone = (
    date: Date | string,
    timezoneId: string,
    options: Intl.DateTimeFormatOptions
): string => {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
        return '';
    }

    try {
        return new Intl.DateTimeFormat('en-US', {
            timeZone: timezoneId,
            ...options
        }).format(d);
    } catch (error) {
        console.error(`Error formatting date in timezone ${timezoneId}:`, error);
        // Fallback to local time if timezone is invalid
        return new Intl.DateTimeFormat('en-US', options).format(d);
    }
};

/**
 * Formats a date to ISO 8601 format with the timezone offset of the specified timezone ID.
 * Example: '2026-05-12T22:00:00Z' in 'America/New_York' -> '2026-05-12T18:00:00-04:00'
 * 
 * @param date - Date object or ISO string
 * @param timezoneId - IANA timezone ID (e.g., "America/New_York")
 * @returns ISO string with timezone offset
 */
export const formatISOWithTimezone = (date: Date | string, timezoneId: string): string => {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
        throw new Error('Invalid date provided to formatISOWithTimezone');
    }

    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezoneId,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            fractionalSecondDigits: 3,
            hour12: false,
            timeZoneName: 'longOffset',
        });

        const parts = formatter.formatToParts(d);
        const partValues: Record<string, string> = {};
        for (const part of parts) {
            if (part.type !== 'literal') {
                partValues[part.type] = part.value;
            }
        }

        // Handle 24:00:00 case in some browsers
        if (partValues.hour === '24') {
            partValues.hour = '00';
        }

        const fraction = partValues.fractionalSecond || '000';
        const localTime = `${partValues.year}-${partValues.month}-${partValues.day}T${partValues.hour}:${partValues.minute}:${partValues.second}.${fraction}`;

        let offset = partValues.timeZoneName;
        if (!offset || offset === 'GMT') {
            offset = 'Z';
        } else {
            // "GMT-04:00" -> "-04:00"
            offset = offset.replace('GMT', '');
        }

        return `${localTime}${offset}`;
    } catch (error) {
        console.error(`Error formatting ISO date with timezone ${timezoneId}:`, error);
        // Fallback to basic ISO string if timezone validation fails
        return d.toISOString();
    }
};
